<template>
  <div class="st-page" v-loading="loading">

    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">

        <!-- Content header -->
        <div class="st-content-head">
          <div>
            <div class="st-content-title">API Keys</div>
            <div class="st-content-sub">
              {{ activeCount }} active key{{ activeCount !== 1 ? 's' : '' }}
            </div>
          </div>
          <div class="st-head-actions">
            <button class="st-btn" @click="openCreate">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Create API key
            </button>
          </div>
        </div>

        <!-- Intro -->
        <p class="ak-intro">
          Give an external service, like a supplier's ordering system, permission to place and
          track orders on your account without sharing your password. Keys are limited to orders
          only and can be revoked anytime.
        </p>

        <!-- Table -->
        <div class="st-table">
          <div class="st-thead">
            <div class="st-th">Name</div>
            <div class="st-th">Key</div>
            <div class="st-th">Scope</div>
            <div class="st-th">Last used</div>
            <div class="st-th">Created</div>
            <div class="st-th">Status</div>
            <div class="st-th st-th--end"></div>
          </div>

          <div class="st-tbody">
            <div v-for="row in keys" :key="row.id" class="st-row-wrap">
              <div class="st-row" :class="{ 'st-row--off': row.revoked }">
                <div class="st-td"><span class="ak-name">{{ row.name }}</span></div>
                <div class="st-td"><span class="ak-key">{{ row.key_prefix }}…</span></div>
                <div class="st-td">
                  <span class="ak-scope">{{ row.scope }}</span>
                </div>
                <div class="st-td"><span class="st-muted">{{ fmtDateTime(row.last_used_at) || 'Never' }}</span></div>
                <div class="st-td"><span class="st-muted">{{ fmtDate(row.created_at) }}</span></div>
                <div class="st-td">
                  <span v-if="row.revoked" class="ak-status ak-status--revoked">Revoked</span>
                  <span v-else class="ak-status ak-status--active">Active</span>
                </div>
                <div class="st-td st-td--end">
                  <span v-if="!row.revoked" class="st-action-link st-action-link--revoke" @click="askRevoke(row)">
                    Revoke
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="st-empty" v-if="!loading && keys.length === 0">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccdeff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
            <div class="st-empty-text">No API keys yet</div>
            <button class="st-btn" @click="openCreate">Create your first key</button>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Create key modal -->
  <Teleport to="body">
    <div v-if="createModal.open" class="st-confirm-overlay" @click.self="closeCreate">
      <div class="st-confirm-card st-confirm-card--form">
        <div class="ak-form-head">
          <div class="st-confirm-icon st-confirm-icon--key">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
          </div>
          <div class="st-confirm-title">Create API key</div>
          <div class="st-confirm-msg">Give the key a name so you can recognise it later — for example, the service that will use it.</div>
        </div>
        <div class="ak-field">
          <label class="st-label">Key name</label>
          <input
            ref="nameInputRef"
            v-model="createModal.name"
            class="st-input"
            placeholder="e.g. Supplier ordering system"
            maxlength="80"
            @keydown.enter.prevent="submitCreate"
          />
          <p class="billing-error" v-if="createModal.error">{{ createModal.error }}</p>
        </div>
        <div class="st-confirm-actions">
          <button class="st-confirm-btn st-confirm-btn--ghost" @click="closeCreate" :disabled="createModal.saving">Cancel</button>
          <button class="st-confirm-btn st-confirm-btn--primary" @click="submitCreate" :disabled="createModal.saving">
            {{ createModal.saving ? 'Creating…' : 'Create key' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Reveal key modal (shown once) -->
  <Teleport to="body">
    <div v-if="revealModal.open" class="st-confirm-overlay">
      <div class="st-confirm-card st-confirm-card--form">
        <div class="ak-form-head">
          <div class="st-confirm-icon st-confirm-icon--ok">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div class="st-confirm-title">API key created</div>
        </div>

        <div class="ak-warning">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>Copy this key now — it will not be shown again.</span>
        </div>

        <div class="ak-key-reveal">
          <code class="ak-key-value">{{ revealModal.key }}</code>
          <button class="ak-copy-btn" :class="{ 'ak-copy-btn--done': copied }" @click="copyKey">
            <svg v-if="!copied" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>

        <div class="st-confirm-actions">
          <button class="st-confirm-btn st-confirm-btn--primary" @click="closeReveal">Done</button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Revoke confirm modal -->
  <Teleport to="body">
    <div v-if="revokeModal.open" class="st-confirm-overlay" @click.self="revokeModal.open = false">
      <div class="st-confirm-card">
        <div class="st-confirm-icon st-confirm-icon--warn">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
        </div>
        <div class="st-confirm-title">Revoke this key?</div>
        <div class="st-confirm-msg">
          This immediately disables <strong>{{ revokeModal.row?.name }}</strong>; any service using it will stop working.
        </div>
        <div class="st-confirm-actions">
          <button class="st-confirm-btn st-confirm-btn--ghost" @click="revokeModal.open = false" :disabled="revokeModal.saving">Cancel</button>
          <button class="st-confirm-btn st-confirm-btn--danger" @click="doRevoke" :disabled="revokeModal.saving">
            {{ revokeModal.saving ? 'Revoking…' : 'Revoke key' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { DateTime } from 'luxon';
import { message as ElMessage } from '../../utils/message';
import api from '../../api';
import SettingsSidebar from '../settings/SettingsSidebar.vue';

interface ApiKey {
  id: number | string;
  name: string;
  scope: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
  revoked: boolean;
}

const loading = ref(true);
const keys    = ref<ApiKey[]>([]);

const activeCount = computed(() => keys.value.filter(k => !k.revoked).length);

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  let dt = DateTime.fromISO(d, { zone: 'UTC' });
  if (!dt.isValid) dt = DateTime.fromSQL(d, { zone: 'UTC' });
  return dt.isValid ? dt.toFormat('dd MMM yyyy') : '—';
};

const fmtDateTime = (d: string | null) => {
  if (!d) return '';
  let dt = DateTime.fromISO(d, { zone: 'UTC' });
  if (!dt.isValid) dt = DateTime.fromSQL(d, { zone: 'UTC' });
  return dt.isValid ? dt.toFormat('dd MMM yyyy, HH:mm') : '';
};

const fetchKeys = async () => {
  try {
    const res = await api.get('customer/api-keys');
    keys.value = res.data.data.keys ?? [];
  } catch {
    ElMessage.error('Failed to load API keys.');
  } finally {
    loading.value = false;
  }
};

// ── Create ────────────────────────────────────────────────────────────────────
const nameInputRef = ref<HTMLInputElement>();
const createModal = reactive({ open: false, name: '', saving: false, error: '' });

const openCreate = () => {
  createModal.name   = '';
  createModal.error  = '';
  createModal.saving = false;
  createModal.open   = true;
  nextTick(() => nameInputRef.value?.focus());
};

const closeCreate = () => {
  if (createModal.saving) return;
  createModal.open = false;
};

const submitCreate = async () => {
  if (createModal.saving) return;
  const name = createModal.name.trim();
  if (!name) { createModal.error = 'Enter a name for this key.'; return; }
  createModal.error  = '';
  createModal.saving = true;
  try {
    const res  = await api.post('customer/api-keys', { name });
    const data = res.data.data;
    createModal.open = false;
    openReveal(data.key);
    ElMessage.success('API key created');
    fetchKeys();
  } catch (err: any) {
    createModal.error = err.response?.data?.message || 'Could not create the key. Please try again.';
  } finally {
    createModal.saving = false;
  }
};

// ── Reveal (one-time secret) ────────────────────────────────────────────────────
const copied = ref(false);
const revealModal = reactive({ open: false, key: '' });

const openReveal = (key: string) => {
  copied.value      = false;
  revealModal.key   = key;
  revealModal.open  = true;
};

const closeReveal = () => {
  revealModal.open = false;
  revealModal.key  = '';
  copied.value     = false;
};

const copyKey = async () => {
  const value = revealModal.key;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
    } else {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    copied.value = true;
    ElMessage.success('API key copied to clipboard');
    setTimeout(() => { copied.value = false; }, 2500);
  } catch {
    ElMessage.error('Could not copy the key. Please copy it manually.');
  }
};

// ── Revoke ──────────────────────────────────────────────────────────────────────
const revokeModal = reactive({ open: false, row: null as ApiKey | null, saving: false });

const askRevoke = (row: ApiKey) => {
  revokeModal.row    = row;
  revokeModal.saving = false;
  revokeModal.open   = true;
};

const doRevoke = async () => {
  const row = revokeModal.row;
  if (!row || revokeModal.saving) return;
  revokeModal.saving = true;
  try {
    await api.delete(`customer/api-keys/${row.id}`);
    ElMessage.success('API key revoked');
    revokeModal.open = false;
    fetchKeys();
  } catch (err: any) {
    ElMessage.error(err.response?.data?.message || 'Could not revoke the key. Please try again.');
  } finally {
    revokeModal.saving = false;
  }
};

onMounted(fetchKeys);
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

/* Content header */
.st-content-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
}
.st-content-title { font-size: 1rem; font-weight: 700; color: #0f1729; margin-bottom: 2px; }
.st-content-sub { font-size: 0.78rem; color: #94a3b8; }
.st-head-actions { display: flex; gap: 8px; align-items: center; }

.st-btn {
  height: 38px; padding: 0 16px;
  background: #0061eb; color: white; border: none; border-radius: 8px;
  font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: background 0.15s; white-space: nowrap;
}
.st-btn:hover { background: #0050c7; }

.ak-intro {
  font-size: 0.83rem; color: #64748b; line-height: 1.5;
  max-width: 720px; margin: 0 0 18px;
}

/* Table */
.st-table { background: white; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; }
.st-thead {
  display: grid; grid-template-columns: 1.1fr 1.3fr 90px 160px 130px 100px 100px;
  background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; padding: 0 20px;
}
.st-th {
  font-size: 0.68rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em; padding: 12px 10px;
}
.st-th--end { text-align: right; }

.st-tbody { display: flex; flex-direction: column; }
.st-row-wrap { border-bottom: 1px solid #e8f0ff; }
.st-row-wrap:last-child { border-bottom: none; }
.st-row {
  display: grid; grid-template-columns: 1.1fr 1.3fr 90px 160px 130px 100px 100px;
  align-items: center; padding: 0 20px; height: 56px; transition: background 0.12s;
}
.st-row:hover { background: #f0f6ff; }
.st-row--off { opacity: 0.62; }
.st-td { padding: 0 10px; overflow: hidden; }
.st-td--end { display: flex; justify-content: flex-end; align-items: center; }

.ak-name { font-size: 0.84rem; font-weight: 600; color: #0f1729; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
.ak-key {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.8rem; color: #334155; letter-spacing: 0.01em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
}
.st-muted { font-size: 0.81rem; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }

.ak-scope {
  display: inline-flex; align-items: center; padding: 3px 10px;
  border-radius: 20px; font-size: 0.72rem; font-weight: 600; white-space: nowrap; border: 1.5px solid;
  color: #2563eb; background: #eff6ff; border-color: #93c5fd; text-transform: lowercase;
}

.ak-status {
  display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px;
  border-radius: 20px; font-size: 0.72rem; font-weight: 700; white-space: nowrap; border: 1.5px solid;
}
.ak-status--active { color: #16a34a; background: #f0fdf4; border-color: #bbf7d0; }
.ak-status--revoked { color: #dc2626; background: #fef2f2; border-color: #fecaca; }

.st-action-link {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.78rem; font-weight: 600; color: #ef4444;
  cursor: pointer; transition: gap 0.15s, opacity 0.15s; white-space: nowrap; opacity: 0.7;
}
.st-action-link--revoke { color: #dc2626; }
.st-row:hover .st-action-link { gap: 7px; opacity: 1; }

.st-empty {
  text-align: center; padding: 56px 0;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.st-empty-text { font-size: 0.9rem; font-weight: 500; color: #94a3b8; }

/* Modals */
.st-confirm-overlay {
  position: fixed; inset: 0; z-index: 3000;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Sans', sans-serif; padding: 16px;
}
.st-confirm-card {
  background: #fff; border-radius: 16px; padding: 26px 24px 20px;
  width: 380px; max-width: 100%; text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
.st-confirm-card--form { width: 440px; text-align: left; }
.ak-form-head { text-align: center; }
.st-confirm-icon {
  width: 48px; height: 48px; border-radius: 50%; margin: 0 auto 14px;
  display: flex; align-items: center; justify-content: center;
}
.st-confirm-icon--warn { background: #fef3c7; color: #d97706; }
.st-confirm-icon--ok { background: #dcfce7; color: #16a34a; }
.st-confirm-icon--key { background: #e8f0ff; color: #0061eb; }
.st-confirm-title { font-size: 1.05rem; font-weight: 700; color: #0f1729; margin-bottom: 6px; text-align: center; }
.st-confirm-msg { font-size: 0.85rem; color: #64748b; line-height: 1.45; margin-bottom: 20px; text-align: center; }
.st-confirm-actions { display: flex; gap: 10px; margin-top: 6px; }
.st-confirm-btn {
  flex: 1; padding: 10px; border-radius: 10px; border: none;
  font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 700;
  cursor: pointer; transition: background 0.15s, opacity 0.15s;
}
.st-confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.st-confirm-btn--ghost { background: #f1f5f9; color: #475569; }
.st-confirm-btn--ghost:hover { background: #e2e8f0; }
.st-confirm-btn--primary { background: #0061eb; color: #fff; }
.st-confirm-btn--primary:hover { background: #0050c7; }
.st-confirm-btn--danger { background: #dc2626; color: #fff; }
.st-confirm-btn--danger:hover { background: #b91c1c; }

/* Create form field */
.ak-field { margin-bottom: 18px; }
.st-label { font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; display: block; }
.st-input {
  width: 100%; height: 42px; padding: 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.st-input:focus { border-color: #0061eb; box-shadow: 0 0 0 3px rgba(0,97,235,0.08); }
.st-input::placeholder { color: #94a3b8; }
.billing-error { font-size: 0.75rem; color: #dc2626; margin-top: 6px; }

/* Reveal key */
.ak-warning {
  display: flex; align-items: flex-start; gap: 8px;
  background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 10px;
  padding: 10px 12px; margin-bottom: 14px;
  font-size: 0.8rem; font-weight: 600; color: #b45309; line-height: 1.4;
}
.ak-warning svg { flex-shrink: 0; margin-top: 1px; }
.ak-key-reveal {
  display: flex; align-items: center; gap: 8px;
  background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px;
  padding: 8px 8px 8px 12px; margin-bottom: 4px;
}
.ak-key-value {
  flex: 1; min-width: 0;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.82rem; color: #0f1729; word-break: break-all; line-height: 1.4;
}
.ak-copy-btn {
  flex-shrink: 0; height: 34px; padding: 0 14px;
  background: #0061eb; color: white; border: none; border-radius: 8px;
  font-family: 'DM Sans', sans-serif; font-size: 0.78rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background 0.15s;
}
.ak-copy-btn:hover { background: #0050c7; }
.ak-copy-btn--done { background: #16a34a; }
.ak-copy-btn--done:hover { background: #15803d; }
</style>
